import { useCallback } from "react"
import { apiFetch } from "../../../utils/apiFetch"

export default function useAgendaDragAndDrop({
  API_BASE,
  clinicId,
  date,
  dragConflictInfo,
  setDragConflictInfo,
  setDragAlternatives,
  setLoadingDragAlternatives,
  loadHeldSlots,
  loadAgenda,
  isReadOnly,
  readOnlyReason
}) {
  const refreshAgendaView = useCallback(
    async (targetClinicId = clinicId, targetDate = date) => {
      if (!targetClinicId) return

      const freshHeld = await loadHeldSlots(targetClinicId, targetDate)
      await loadAgenda(targetClinicId, targetDate, freshHeld)
    },
    [clinicId, date, loadHeldSlots, loadAgenda]
  )

  const saveAppointmentChange = useCallback(
    async (info) => {
      try {
        const res = await apiFetch(`${API_BASE}/appointments/reschedule`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            appointmentId: info.event.id,
            doctorId: info.event.extendedProps?.doctorId || undefined,
            startTime:
              info.event.start instanceof Date
                ? info.event.start.toISOString()
                : info.event.start,
            endTime:
              info.event.end instanceof Date
                ? info.event.end.toISOString()
                : info.event.end
          })
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          info.revert()
          return {
            ok: false,
            message: data?.message || "No se pudo actualizar la cita"
          }
        }

        await refreshAgendaView()
        return { ok: true }
      } catch (error) {
        info.revert()
        return {
          ok: false,
          message: error?.message || "Error al actualizar la cita"
        }
      }
    },
    [API_BASE, refreshAgendaView]
  )

  const loadDragAlternatives = useCallback(
    async (eventInfo) => {
      try {
        setLoadingDragAlternatives?.(true)

        const movedEvent = eventInfo.event
        const treatmentId = movedEvent.extendedProps?.treatmentId || ""
        console.log("DRAG treatmentId:", treatmentId)
        console.log("EVENT PROPS:", movedEvent.extendedProps)

        if (!treatmentId) {
          setDragAlternatives([])
          setDragConflictInfo({
            title: movedEvent.title,
            start: movedEvent.start,
            end: movedEvent.end,
            message: "No se pudo identificar el tratamiento para sugerir alternativas"
          })
          return
        }

        const durationMinutes = Math.max(
          10,
          Math.round(
            (new Date(movedEvent.end).getTime() - new Date(movedEvent.start).getTime()) / 60000
          )
        )

        const res = await apiFetch(`${API_BASE}/appointments/suggest-alternatives`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            clinicId,
            treatmentId,
            desiredStart: new Date(movedEvent.start).toISOString(),
            durationMinutes,
            excludeAppointmentId: movedEvent.id
          })
        })

        const data = await res.json().catch(() => null)
        console.log("DRAG ALT RESPONSE:", data)

        setDragConflictInfo({
          appointmentId: movedEvent.id,
          title: movedEvent.title,
          start: movedEvent.start,
          end: movedEvent.end,
          treatmentId
        })

        setDragAlternatives(Array.isArray(data?.suggestions) ? data.suggestions : [])
      } catch {
        setDragAlternatives([])
      } finally {
        setLoadingDragAlternatives?.(false)
      }
    },
    [
      API_BASE,
      clinicId,
      setDragConflictInfo,
      setDragAlternatives,
      setLoadingDragAlternatives
    ]
  )

  const handleEventDrop = useCallback(
    async (info) => {
      if (isReadOnly) {
        info.revert()
        alert(readOnlyReason || "Modo solo lectura activo")
        return
      }

      if (String(info.event.id).startsWith("held-")) {
        info.revert()
        alert("Una reserva temporal no se puede mover desde aquí")
        return
      }

      const result = await saveAppointmentChange(info)

      if (!result.ok) {
        alert(result.message)
        await loadDragAlternatives(info)
        return
      }

      setDragConflictInfo(null)
      setDragAlternatives([])
    },
    [
      isReadOnly,
      readOnlyReason,
      saveAppointmentChange,
      loadDragAlternatives,
      setDragConflictInfo,
      setDragAlternatives
    ]
  )

  const handleEventResize = useCallback(
    async (info) => {
      if (isReadOnly) {
        info.revert()
        alert(readOnlyReason || "Modo solo lectura activo")
        return
      }

      if (String(info.event.id).startsWith("held-")) {
        info.revert()
        alert("Una reserva temporal no se puede redimensionar desde aquí")
        return
      }

      const result = await saveAppointmentChange(info)

      if (!result.ok) {
        alert(result.message)
        await loadDragAlternatives(info)
        return
      }

      setDragConflictInfo(null)
      setDragAlternatives([])
    },
    [
      isReadOnly,
      readOnlyReason,
      saveAppointmentChange,
      loadDragAlternatives,
      setDragConflictInfo,
      setDragAlternatives
    ]
  )

  const applyDragAlternative = useCallback(
    async (alt) => {
      if (isReadOnly) {
        alert(readOnlyReason || "Modo solo lectura activo")
        return
      }

      if (!dragConflictInfo?.appointmentId) return

      try {
        const res = await apiFetch(`${API_BASE}/appointments/reschedule`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            appointmentId: dragConflictInfo.appointmentId,
            doctorId: alt.doctorId,
            startTime: new Date(alt.startTime).toISOString(),
            endTime: new Date(alt.endTime).toISOString()
          })
        })

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          alert(data?.message || "No se pudo aplicar la alternativa")
          return
        }

        setDragConflictInfo(null)
        setDragAlternatives([])
        await refreshAgendaView()
      } catch (error) {
        alert(error?.message || "Error al aplicar alternativa")
      }
    },
    [
      isReadOnly,
      readOnlyReason,
      API_BASE,
      dragConflictInfo,
      setDragConflictInfo,
      setDragAlternatives,
      refreshAgendaView
    ]
  )

  return {
    saveAppointmentChange,
    loadDragAlternatives,
    handleEventDrop,
    handleEventResize,
    applyDragAlternative,
    refreshAgendaView
  }
}